import {
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    ElementRef,
    Input,
    OnDestroy,
    OnInit,
    QueryList,
    ViewChild,
    ViewChildren,
    forwardRef
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Observable, Subject, combineLatest } from 'rxjs';
import { delay, filter, map, takeUntil } from 'rxjs/operators';
import { SPP_OVERLAY_POSITION_DROPDOWN } from '../../common-ui-common.config';

/**
 * Filterkomponente für Tabellenspalten, die es ermöglich, in eine Liste von festen Werten einen oder mehrere per Checkbox auszuwählen.
 *
 * @author femu
 * @author stoc
 */
@Component({
    selector: 'common-table-multi-select-filter',
    templateUrl: 'table-multi-select-filter.component.html',
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => TableMultiSelectFilterComponent),
            multi: true
        }
    ]
})
export class TableMultiSelectFilterComponent implements ControlValueAccessor, AfterViewInit, OnDestroy, OnInit {

    /**
     * Die Werte, die der Nutzer auswählen können soll.
     */
    @Input() items: MultiSelectItem[] = [];

    /**
     * Gibt an, ob die Auswahl von {@link #items} auf maximal einen Wert eingeschränkt ist.
     * So können z.B. auch Boolean-Filter umgesetzt werden.
     * Der Wert den der Filter bei single = true liefert ist dennoch ein Array. Dieses ist entweder
     * leer oder enthält den einen selektierten Wert.
     */
    @Input() single: boolean = false;

    /**
     * Ob mindestens ein Item selektiert ist
     */
    anySelected: boolean;

    /**
     * Map, die für die selektierten Einträge true als Values enthält.
     */
    selected: { [propName: string]: boolean } = {};

    /**
     * Ob das Overlay, dass die Auswahlmöglichkeiten anzeigt, gerade offen ist.
     */
    isOpen: boolean = false;

    overlayPosition = SPP_OVERLAY_POSITION_DROPDOWN;

    disabled: boolean = false;

    @ViewChildren('containerFocusElement', {read: ElementRef})
    private containerFocusElement: QueryList<ElementRef>;

    @ViewChild('focusElement', {static: true})
    private focusElement: ElementRef;

    private onChange: (values: any[]) => void;

    private dropdownOpen$ = new Subject<void>();

    private ngUnsubscribe$ = new Subject<void>();

    private keyboardEscape$ = new Subject<void>();

    private keyboardSpace$ = new Subject<{ event: Event, item: MultiSelectItem }>();

    constructor(
        private readonly ci: ChangeDetectorRef) {
    }


    ngOnInit() {
        // Mark on keyboard "Space" mark corresponding item as selected
        this.keyboardSpace$
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(x => {
                x.event.preventDefault();
                this.selectItem(x.item);
                this.ci.markForCheck();
                this.onChecked();
            });
    }

    ngAfterViewInit() {
        // In order to activate keyboard inside of cdk overlay selecting first element inside of the overlay.
        combineLatest([
            this.getFocusElement(),
            this.dropdownOpen$
        ])
            .pipe(
                delay(10),
                takeUntil(this.ngUnsubscribe$)
            )
            .subscribe(([el]) => el.focus());
    }

    ngOnDestroy() {
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.unsubscribe();
        this.dropdownOpen$.unsubscribe();
        this.keyboardSpace$.unsubscribe();
        this.keyboardEscape$.unsubscribe();
    }

    onChecked() {
        this.anySelected = !!this.items.find(item => this.selected[item.name]);

        // gib alle selektierten Einträge zurück
        this.onChange(this.items.filter(item => this.selected[item.name]).map(item => item.name));
    }

    open() {
        this.isOpen = true;
    }

    close() {
        this.isOpen = false;
        this.focusElement.nativeElement.focus();
    }

    writeValue(values: string[]): void {
        // Hier findet die Vorselektierung statt.
        this.items.forEach(item => {
            this.selected[item.name] = (values || []).indexOf(item.name) !== -1;
        });
        this.anySelected = !!this.items.find(item => this.selected[item.name]);
    }

    registerOnChange(fn: any): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: any): void {

    }

    setDisabledState(isDisabled: boolean) {
        this.disabled = isDisabled;
    }

    onKeyboardEnter(): void {
        this.close();
    }

    onKeyboardSpace(event: Event, item: MultiSelectItem): void {
        event.preventDefault();
        this.keyboardSpace$.next({event, item});
    }

    onKeyboardDown(event: Event): void {
        event.preventDefault();
        this.isOpen = true;
        if (Object.keys(this.selected).length < 1) {
            return;
        }
        this.ci.markForCheck();
    }

    onDropdownOpen(): void {
        this.dropdownOpen$.next();
    }

    onKeyboardEscape(): void {
        this.keyboardEscape$.next();
    }

    onKeyboardListThresholdTop(): void {
        this.close();
    }

    /**
     * Kommaseparierter String aller selektierten Werte. Für Anzeige im Header.
     */
    get selectedCommaSeparated() {
        return Object.keys(this.selected)
            .filter(k => !!this.selected[k])
            .map(k => this.items.find(i => i.name == k)?.displayName)
            .join(',');
    }

    private selectItem(item: MultiSelectItem): void {
        this.selected[item.name] = !this.selected[item.name];
    }

    private getFocusElement(): Observable<HTMLDivElement> {
        return this.containerFocusElement.changes.pipe(
            map(qlist => qlist.first),
            filter((elRef: ElementRef) => !!elRef),
            map(elRef => elRef.nativeElement),
            filter(el => !!el),
        );
    }

}

/**
 * Beschreibt die interne Struktur eines MultiSelectItems in
 * {@link TableMultiSelectFilterComponent}. Nicht verwechseln mit
 * {@link MultiselectionsItemsInput}.
 */
export interface MultiSelectItem {
    name: string;
    displayName: string;
}
